
import Vue from "vue";
import {
  Component,
  Inject,
  Prop,
  Provide,
  Watch
} from "vue-property-decorator";
import lodash from "lodash";
import {
  ChangeControlValueMeta,
  Control,
  ControlsTreeContext,
  CustomTypographyOptions,
  decorateValueEmittedByInputWithMetadata
} from "@/types/controls";
import {
  CustomTypographyInputValue,
  CustomTypographyInputValueV,
  FontWeightV
} from "@/types/typography";
import defensiveCopy from "@/util/defensive-copy";
import ButtonBarInput from "@/components/Reusable/CustomInputs/ButtonBarInput/ButtonBarInput.vue";
import GlobalTypographySaveDialog from "@/components/Reusable/CustomInputs/Typography/GlobalTypographySaveDialog.vue";
import { isObject } from "@/util/objects";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";

@Component({
  components: {
    GlobalTypographySaveDialog,
    ButtonBarInput,
    ControlsTreeExpansionPanels: () =>
      import(
        "@/components/Reusable/ControlsTree/ControlsTreeExpansionPanels.vue"
      )
  }
})
export default class CustomTypographyInput extends Vue {
  // can be undefined, which breaks reactivity, so we default to null
  @Prop({ default: null })
  value!: CustomTypographyInputValue | null;

  @Prop({
    default: () => {
      return {};
    }
  })
  options!: CustomTypographyOptions;

  /** relying on prop/emit to update its value, instead of directly the store like normal ControlsTreeContext causes race conditions
   * when changing two values very fast. See https://github.com/soflyy/breakdance/issues/1916
   * Using internalValue solves that by updating instantly
   */
  internalValue = this.value;

  @Watch("value")
  updateInternalValueWhenValueChanges() {
    this.internalValue = this.value;
  }

  get validValue(): CustomTypographyInputValue {
    return this.validateCustomTypographyInputValueOrReturnFallback(
      this.internalValue,
      {
        customTypography: {}
      }
    );
  }

  /**
   * provider/inject are not reactive by default
   * By giving it an object that is reactive (because it has state/computer props inside of it) we make it so
   *
   * In other words, this object cannot be given to the "context" directly, otherwise hell breaks lose
   * https://github.com/soflyy/breakdance/issues/1550
   */
  reactiveProvider(): ControlsTreeContext {
    return {
      getControlValue: controlPath => {
        return lodash.get(defensiveCopy(this.validValue), controlPath);
      },
      setControlValue: (
        controlPathProbablyWithBreakpointIdAndHoverSuffix,
        newValue,
        control,
        meta?: ChangeControlValueMeta
      ) => {
        const newTypographyValue = defensiveCopy(
          isObject(this.validValue) ? this.validValue : {}
        );

        lodash.set(
          newTypographyValue,
          controlPathProbablyWithBreakpointIdAndHoverSuffix,
          newValue
        );

        this.emit(newTypographyValue, {
          nestedControlThatWasChanged: {
            path: controlPathProbablyWithBreakpointIdAndHoverSuffix,
            control,
            controlsTree: this.typographyControls,
            meta
          }
        });
      },
      getControls: (): Control[] | undefined => {
        return this.typographyControls;
      },
      setAccordionState: (accordionPath: string, open: boolean) => {
        return this.context.setAccordionState(accordionPath, open);
      },
      getAccordionState: (accordionPath: string) => {
        return this.context.getAccordionState(accordionPath);
      }
    };
  }

  validateCustomTypographyInputValueOrReturnFallback(
    value: unknown,
    fallback: CustomTypographyInputValue
  ): CustomTypographyInputValue {
    return pipe(
      value,
      CustomTypographyInputValueV.decode,
      fold(
        () => {
          console.warn(
            "CustomTypographyInput decoder says invalid value... falling back to ",
            fallback
          );
          return fallback;
        },
        validValue => {
          return validValue;
        }
      )
    );
  }

  @Inject("context") readonly context!: ControlsTreeContext;

  @Provide("context")
  typographyControlsTreeContext = this.reactiveProvider();

  emit(value: unknown, metadata?: ChangeControlValueMeta) {
    this.internalValue =
      this.validateCustomTypographyInputValueOrReturnFallback(value, {
        customTypography: {}
      });

    this.$emit(
      "input",
      metadata
        ? decorateValueEmittedByInputWithMetadata(this.internalValue, metadata)
        : this.internalValue
    );
  }

  get typographyControls(): Control[] {
    const layout = "inline";
    const children: Control[] = [];
    const enableMediaQueries = !!this.options.enableMediaQueries;
    const enableHover = !!this.options.hoverableEverything;
    const enableVariables = !!this.options.enableVariables;

    return [
      {
        slug: "fontFamily",
        label: "Font Family",
        keywords: [],
        options: { layout, type: "font_family" },
        children,
        enableMediaQueries,
        enableHover,
        enableVariables
      },
      {
        slug: "fontSize",
        label: "Font Size",
        keywords: [],
        options: {
          layout,
          type: "unit",
          rangeOptions: { min: 10, max: 128, step: 1 }
        },
        children,
        enableMediaQueries,
        enableHover,
        enableVariables
      },
      {
        slug: "fontWeight",
        label: "Weight",
        keywords: [],
        options: {
          layout,
          type: "dropdown",
          items: FontWeightV.types.map(fontWeight => ({
            text: fontWeight.value,
            value: fontWeight.value
          }))
        },
        children,
        enableMediaQueries,
        enableHover,
        enableVariables
      },
      {
        slug: "lineHeight",
        label: "Line Height",
        keywords: [],
        children: [],
        options: {
          layout,
          type: "unit",
          rangeOptions: { step: 0.1, min: 1, max: 3 },
          unitOptions: {
            defaultType: "custom",
            disableCustomUnitDropdown: true
          },
          condition: {
            path: "customTypography.advanced.lineHeight",
            operand: "is not set",
            value: ""
          }
        },
        enableMediaQueries,
        enableHover,
        enableVariables
      },
      {
        slug: "advanced",
        label: "Advanced",
        keywords: [],
        options: {
          type: "section",
          sectionOptions: {
            type: "popout"
          }
        },
        enableMediaQueries,
        enableHover,
        enableVariables,
        children: [
          {
            slug: "lineHeight",
            label: "Line Height (deprecated)",
            keywords: [],
            children: [],
            options: {
              layout,
              type: "unit",
              rangeOptions: { step: 0.1, min: 1, max: 3 },
              unitOptions: {
                defaultType: "custom",
                disableCustomUnitDropdown: true
              },
              condition: {
                path: "customTypography.advanced.lineHeight",
                operand: "is set",
                value: ""
              }
            },
            enableMediaQueries,
            enableHover,
            enableVariables
          },
          {
            slug: "letterSpacing",
            label: "Letter Spacing",
            keywords: [],
            options: {
              layout,
              type: "unit",
              rangeOptions: { step: 0.1, min: 0.1, max: 3 }
            },
            children,
            enableMediaQueries,
            enableHover,
            enableVariables
          },
          {
            slug: "wordSpacing",
            label: "Word Spacing",
            keywords: [],
            options: {
              layout,
              type: "unit",
              rangeOptions: { step: 0.1, min: 0.1, max: 3 }
            },
            children,
            enableMediaQueries,
            enableHover,
            enableVariables
          },
          {
            slug: "fontStyle",
            label: "Style",
            keywords: [],
            options: {
              layout,
              type: "button_bar",
              items: [
                { text: "normal", value: "normal", icon: "FontIcon" },
                { text: "italic", value: "italic", icon: "ItalicIcon" }
              ]
            },
            children,
            enableMediaQueries,
            enableHover,
            enableVariables
          },
          {
            slug: "decoration",
            label: "Decoration",
            keywords: [],
            options: {
              type: "section",
              sectionOptions: {
                type: "popout"
              }
            },
            enableMediaQueries: false,
            enableHover: false,
            enableVariables: false,
            children: [
              {
                slug: "line",
                label: "Line",
                options: {
                  layout: "vertical",
                  type: "button_bar",
                  buttonBarOptions: { multiple: true },
                  items: [
                    {
                      text: "underline",
                      value: "underline",
                      icon: "UnderlineIcon"
                    },
                    {
                      text: "overline",
                      value: "overline",
                      icon: "OverlineIcon"
                    },
                    {
                      text: "line-through",
                      value: "line-through",
                      icon: "StrikethroughIcon"
                    },
                    {
                      text: "none",
                      value: "none",
                      icon: "BanIcon"
                    }
                  ]
                },
                keywords: [],
                children,
                enableMediaQueries,
                enableHover,
                enableVariables
              },
              {
                slug: "underline_offset",
                label: "Underline Offset",
                keywords: [],
                options: {
                  type: "unit",
                  layout: "inline"
                  // condition: {
                  //   path: "advanced.decoration.line",
                  //   operand: "includes",
                  //   value: ["underline"]
                  // }
                },
                children,
                enableMediaQueries,
                enableHover,
                enableVariables
              },
              {
                slug: "color",
                label: "Color",
                keywords: [],
                options: {
                  type: "color",
                  layout: "inline"
                },
                children,
                enableMediaQueries,
                enableHover,
                enableVariables
              },
              {
                slug: "style",
                label: "Style",
                keywords: [],
                options: {
                  layout: "inline",
                  type: "dropdown",
                  items: [
                    {
                      text: "solid",
                      value: "solid"
                    },
                    {
                      text: "double",
                      value: "double"
                    },
                    {
                      text: "dotted",
                      value: "dotted"
                    },
                    {
                      text: "dashed",
                      value: "dashed"
                    },
                    {
                      text: "wavy",
                      value: "wavy"
                    }
                  ]
                },
                children,
                enableMediaQueries,
                enableHover,
                enableVariables
              },
              {
                slug: "thickness",
                label: " Thickness",
                keywords: [],
                options: {
                  layout: "inline",
                  type: "unit",
                  unitOptions: {
                    types: ["px", "%", "em", "custom"],
                    defaultType: "px"
                  }
                },
                children,
                enableMediaQueries,
                enableHover,
                enableVariables
              }
            ]
          },
          {
            slug: "textTransform",
            label: "Transform",
            keywords: [],
            options: {
              layout,
              type: "button_bar",
              items: [
                { text: "AA", value: "uppercase" },
                { text: "aa", value: "lowercase" },
                { text: "Aa", value: "capitalize" },
                { text: "none", value: "none" }
              ]
            },
            children,
            enableMediaQueries,
            enableHover,
            enableVariables
          },
          {
            slug: "textDirection",
            label: "Direction",
            keywords: [],
            options: {
              layout,
              type: "button_bar",
              items: [
                { text: "ltr", value: "ltr" },
                { text: "rtl", value: "rtl" }
              ]
            },
            children,
            enableMediaQueries,
            enableHover,
            enableVariables
          },
          {
            slug: "textWrap",
            label: "Text Wrap",
            keywords: [],
            options: {
              layout: "vertical",
              type: "button_bar",
              items: [
                { text: "Wrap", value: "wrap" },
                { text: "Nowrap", value: "nowrap" },
                { text: "Balance", value: "balance" },
                { text: "Pretty", value: "pretty" }
              ]
            },
            children,
            enableMediaQueries,
            enableHover
          }
        ]
      }
    ];
  }
}
